import { useTranslation } from 'react-i18next';
import { Clock, User, FileText, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { ChangelogEntry } from '../types';

interface LocationChangelogProps {
  /** Changelog entries */
  changelog: ChangelogEntry[];
  /** Whether the changelog is loading */
  isLoading?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Format a timestamp for display
 */
function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

/**
 * Format a user name for display
 */
function formatUserName(user: { firstname?: string; lastname?: string } | null): string {
  if (!user) return 'Unknown user';
  const parts = [user.firstname, user.lastname].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'Unknown user';
}

/**
 * Format a field name for display (convert camelCase/snake_case to Title Case)
 */
function formatFieldName(field: string): string {
  // Handle common field mappings
  const fieldMappings: Record<string, string> = {
    name: 'Name',
    room_number: 'Room Number',
    roomNumber: 'Room Number',
    search_name: 'Search Name',
    searchName: 'Search Name',
    abbreviations: 'Abbreviations',
    description: 'Description',
    phone: 'Phone',
    extension: 'Extension',
    fax: 'Fax',
    fax_extension: 'Fax Extension',
    faxExtension: 'Fax Extension',
    disabled: 'Status',
    tags: 'Tags',
    site: 'Site',
    usergroup: 'User Group',
    transmitters: 'Transmitters',
    sensors: 'Sensors',
    image: 'Image',
    coordinates: 'Coordinates',
    latitude: 'Latitude',
    longitude: 'Longitude',
  };

  if (fieldMappings[field]) {
    return fieldMappings[field];
  }

  // Convert camelCase or snake_case to Title Case
  return field
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Format a field value for display
 */
function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) return '(empty)';
  if (typeof value === 'boolean') return value ? 'Active' : 'Inactive';
  if (Array.isArray(value)) {
    if (value.length === 0) return '(none)';
    return value.join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

interface ChangeItemProps {
  field: string;
  oldValue?: unknown;
  newValue?: unknown;
}

function ChangeItem({ field, oldValue, newValue }: ChangeItemProps) {
  const fieldName = formatFieldName(field);
  const oldDisplay = formatFieldValue(oldValue);
  const newDisplay = formatFieldValue(newValue);

  return (
    <div className="text-sm py-1">
      <span className="font-medium text-foreground">{fieldName}:</span>{' '}
      {oldValue !== undefined && (
        <>
          <span className="text-red-600 line-through">{oldDisplay}</span>
          {' '}
        </>
      )}
      {newValue !== undefined && (
        <span className="text-green-600">{newDisplay}</span>
      )}
    </div>
  );
}

interface ChangelogItemProps {
  entry: ChangelogEntry;
  isFirst: boolean;
}

function ChangelogItem({ entry, isFirst }: ChangelogItemProps) {
  const { t } = useTranslation('locations');

  return (
    <div
      className={cn(
        'relative pl-6 pb-6',
        !isFirst && 'pt-0'
      )}
    >
      {/* Timeline line */}
      <div className="absolute left-[9px] top-6 bottom-0 w-0.5 bg-border" />

      {/* Timeline dot */}
      <div
        className={cn(
          'absolute left-0 top-1 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center',
          entry.isOriginal
            ? 'bg-primary border-primary'
            : 'bg-background border-border'
        )}
      >
        {entry.isOriginal ? (
          <FileText className="h-2.5 w-2.5 text-primary-foreground" />
        ) : (
          <div className="w-2 h-2 rounded-full bg-border" />
        )}
      </div>

      {/* Content */}
      <div className="bg-muted/30 rounded-lg p-3">
        {/* Header */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>{formatTimestamp(entry.timestamp)}</span>
          </div>
          <div className="flex items-center gap-1">
            <User className="h-3 w-3" />
            <span>{formatUserName(entry.user)}</span>
          </div>
        </div>

        {/* Changes or creation label */}
        {entry.isOriginal ? (
          <div className="text-sm text-muted-foreground italic">
            {t('changelog.created')}
          </div>
        ) : entry.changes && entry.changes.length > 0 ? (
          <div className="space-y-0.5">
            {entry.changes.map((change, index) => (
              <ChangeItem
                key={`${change.field}-${index}`}
                field={change.field}
                oldValue={change.old}
                newValue={change.new}
              />
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground italic">
            {t('changelog.updated')}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * LocationChangelog - Timeline view of location change history
 */
export function LocationChangelog({
  changelog,
  isLoading = false,
  className,
}: LocationChangelogProps) {
  const { t } = useTranslation('locations');

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (changelog.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-12 text-muted-foreground', className)}>
        <FileText className="h-12 w-12 mb-2" />
        <span>{t('changelog.empty')}</span>
      </div>
    );
  }

  return (
    <ScrollArea className={cn('h-[400px] pr-4', className)}>
      <div className="space-y-0">
        {changelog.map((entry, index) => (
          <ChangelogItem
            key={entry._id || index}
            entry={entry}
            isFirst={index === 0}
          />
        ))}
      </div>
    </ScrollArea>
  );
}

export default LocationChangelog;
